import { PropertyMedia } from "../types/property";

const EPC_PATTERNS = [
  /EPC\s*(?:rating)?\s*[:=]?\s*([A-G])/i,
  /Energy\s+Performance\s+(?:Certificate\s*)?(?:rating)?\s*[:=]?\s*([A-G])/i,
];

const COUNCIL_TAX_PATTERNS = [
  /Council\s+tax\s+(?:band\s*)?[:=]?\s*([A-H])/i,
];

export function parseEpcFromDescription(description: string[]): string | null {
  for (const line of description) {
    for (const pattern of EPC_PATTERNS) {
      const match = line.match(pattern);
      if (match?.[1]) return match[1].toUpperCase();
    }
  }
  return null;
}

export function parseCouncilTaxFromDescription(
  description: string[]
): string | null {
  for (const line of description) {
    for (const pattern of COUNCIL_TAX_PATTERNS) {
      const match = line.match(pattern);
      if (match?.[1]) return match[1].toUpperCase();
    }
  }
  return null;
}

function isPhoto(media: PropertyMedia): boolean {
  return (
    media.media_type === 1 ||
    (media.media_type == null && media.file_type?.toLowerCase() === "image")
  );
}

function isFloorPlan(media: PropertyMedia): boolean {
  return media.media_type === 2;
}

export function splitMediaByType(media: PropertyMedia[] = []): {
  photos: PropertyMedia[];
  floorPlans: PropertyMedia[];
  documents: PropertyMedia[];
} {
  const photos: PropertyMedia[] = [];
  const floorPlans: PropertyMedia[] = [];
  const documents: PropertyMedia[] = [];

  for (const item of media) {
    if (isPhoto(item)) {
      photos.push(item);
    } else if (isFloorPlan(item)) {
      floorPlans.push(item);
    } else {
      documents.push(item);
    }
  }

  return { photos, floorPlans, documents };
}

export function getAvailabilityLabel(
  status: string | null | undefined,
  available_now: boolean,
  date_available: string | null | undefined
): string {
  const normalized = status?.trim().toLowerCase();
  if (normalized === "let agreed" || normalized === "let") {
    return "Let Agreed";
  }
  if (available_now) {
    return "Available Now";
  }
  if (date_available) {
    const date = new Date(date_available);
    if (!Number.isNaN(date.getTime())) {
      return `Available ${date.toLocaleDateString()}`;
    }
  }
  if (status?.trim()) {
    return status.trim();
  }
  return "Available";
}

export function monthlyRentPcm(
  price: number,
  rent_frequency: string | null | undefined
): number {
  const freq = (rent_frequency ?? "Monthly").toLowerCase();
  if (freq === "weekly" || freq === "pw") {
    return Math.round((price * 52) / 12);
  }
  return price;
}

export function isLetAgreedStatus(status: string | null | undefined): boolean {
  const normalized = status?.trim().toLowerCase();
  return normalized === "let agreed" || normalized === "let";
}

export function isEmbeddableVideoUrl(url: string): boolean {
  return /(?:youtube\.com|youtu\.be)/i.test(url) || /matterport\.com/i.test(url);
}

export function getVideoEmbedUrl(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) return null;

  const youtubeMatch = trimmed.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  if (youtubeMatch?.[1]) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
  }

  if (/matterport\.com/i.test(trimmed)) {
    return trimmed;
  }

  return null;
}

export function collectVideoEmbedUrls(
  media: PropertyMedia[] = [],
  virtualTourUrl?: string | null
): string[] {
  const embeds = new Set<string>();

  for (const item of media) {
    if (item.media_url && isEmbeddableVideoUrl(item.media_url)) {
      const embed = getVideoEmbedUrl(item.media_url);
      if (embed) embeds.add(embed);
    }
  }

  if (virtualTourUrl && isEmbeddableVideoUrl(virtualTourUrl)) {
    const embed = getVideoEmbedUrl(virtualTourUrl);
    if (embed) embeds.add(embed);
  }

  return Array.from(embeds);
}
